import * as React from 'react'
import { useEffect, useState } from 'react'
import { t } from 'ttag'
import { ScreenOrientation } from '../../services/api.types'
import { isNative } from '../../common/helpers/isNative'
import { getStoredOrientation, setStoredOrientation } from '../../common/helpers/anonymousA11y'
import { useAnnouncer } from '../a11y/useAnnouncer'
import YumDialog from '../YumDialog/YumDialog'
import YumDialogTitle from '../YumDialogTitle/YumDialogTitle'
import YumDialogContent from '../YumDialogContent/YumDialogContent'
import YumDialogActions from '../YumDialogActions/YumDialogActions'
import YumSmallSelect from '../YumSmallSelect/YumSmallSelect'
import YumButton from '../YumButton/YumButton'

interface IA11ySettingsDialogProps {
  open: boolean
  onClose: () => void
}

export default function A11ySettingsDialog({ open, onClose }: IA11ySettingsDialogProps) {
  const announce = useAnnouncer()
  const [orientation, setOrientation] = useState<ScreenOrientation>(ScreenOrientation.AUTO)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    getStoredOrientation().then((o) => {
      if (cancelled) return
      setOrientation(o)
    })
    return () => {
      cancelled = true
    }
  }, [open])

  const handleOrientationChange = async (e: React.ChangeEvent<{ value: unknown }>) => {
    const value = e.target.value as ScreenOrientation
    setOrientation(value)
    await setStoredOrientation(value)
    announce(value === ScreenOrientation.PORTRAIT ? t`Screen orientation: portrait only` : t`Screen orientation: auto`)
  }

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    marginBottom: '16px',
  }

  return (
    <YumDialog open={open} onClose={onClose} aria-labelledby="a11y-dialog-title">
      <YumDialogTitle handleClose={onClose} id="a11y-dialog-title">
        {t`Accessibility`}
      </YumDialogTitle>
      <YumDialogContent>
        {isNative() && (
          <div style={rowStyle}>
            <span>{t`Screen orientation`}</span>
            <YumSmallSelect
              variant="outlined"
              value={orientation}
              onChange={handleOrientationChange}
              inputProps={{ 'aria-label': t`Screen orientation`, 'data-a11y-label': t`Screen orientation` }}
            >
              <option value={ScreenOrientation.AUTO}>{t`Auto (rotates with device)`}</option>
              <option value={ScreenOrientation.PORTRAIT}>{t`Portrait only`}</option>
            </YumSmallSelect>
          </div>
        )}
        <p style={{ margin: 0, opacity: 0.75 }}>
          {t`To change text size, use your device's system settings (Settings → Display & Brightness → Text Size, or Settings → Accessibility → Larger Text).`}
        </p>
      </YumDialogContent>
      <YumDialogActions>
        <YumButton onClick={onClose}>{t`Done`}</YumButton>
      </YumDialogActions>
    </YumDialog>
  )
}
